/**
 * offline_intent_controller.js — submit a form through PowerSync when there is
 * no network (Stimulus).
 *
 * Online, this does nothing: the form posts exactly as it always has and the
 * server answers with its Turbo Stream. Offline, the form's entries are written
 * as one row in powersync_write_requests instead. PowerSync uploads the row on
 * reconnect and the server applies it through the same service entry point the
 * form's own route calls (_WriteRequestApply in application/api/powersync.py),
 * so validation and business rules run once, on the server, for both paths.
 *
 * Two ways a form reaches here:
 *   * a form the browser or Turbo submits (unit readings, the signature): its
 *     submit event while offline, or Turbo's network error for it;
 *   * a form another script submits itself (the inline description and price
 *     editors, through window.TurboEditModal): that script cancels the submit
 *     event, so it is skipped here, and announces the offline attempt instead
 *     as 'chippie:offline-submit' on the form. This controller answers by
 *     putting the queueing promise on event.detail.queued.
 * The controller sits on the form or on an element containing it. A form
 * inside it that must not wait for a connection carries
 * data-offline-intent-online-only and fails offline as it always has (pricing
 * an enquiry from the Price block: it moves the quote's status).
 *
 * The page is not repainted: Jinja is the only renderer (ADR-010). A value the
 * page renders through an offline slot shows the pending edit in place
 * (offline_slot_controller.js); a modal closes; a toast says the change is on
 * this device and the sync banner counts it as waiting. A refusal on upload
 * comes back through the connector.
 *
 * Values:
 *   operation — the server's name for what this form does ('asset_record').
 *   target    — the ids the form's URL carries ('<job_id>:<asset_id>').
 *   params    — optional; anything else the URL carries, sent as form entries
 *               ({"status": "on_hold"} for a job status form), so the pending
 *               value is in the payload like any typed field.
 *
 * Stamped from <body>: the session's tenant (data-tenant-id, compared
 * fail-closed at apply) and, on a page that shows one record, its version and
 * field fingerprints (data-record-version, data-record-fields), so the edit is
 * checked against the record as the user saw it, like an online save. A form
 * an editor seeded from the device's copy instead carries that copy's version
 * as data-offline-record-version (and data-offline-record-fields), which wins.
 *
 * Without a local database (PowerSync not configured on this host) it stays
 * out of the way and the submit fails as it would have.
 */

import { Controller } from "@hotwired/stimulus";

const QUEUE_SQL = "INSERT INTO powersync_write_requests "
    + "(id, tenant_id, operation, target_id, payload, record_version, record_fields, requested_at) "
    + "VALUES (uuid(), ?, ?, ?, ?, ?, ?, strftime('%Y-%m-%dT%H:%M:%fZ', 'now'))";

export default class extends Controller {
    static values = { operation: String, target: String, params: Object };

    connect() {
        this._onSubmit = (event) => this._submit(event);
        this._onFetchError = (event) => {
            if (!this._onlineOnly(event.target)) this._queue(event.target);
        };
        this._onOfflineSubmit = (event) => {
            event.stopPropagation();
            event.detail.queued = this._queue(event.target);
        };
        this.element.addEventListener('submit', this._onSubmit);
        this.element.addEventListener('turbo:fetch-request-error', this._onFetchError);
        this.element.addEventListener('chippie:offline-submit', this._onOfflineSubmit);
    }

    disconnect() {
        this.element.removeEventListener('submit', this._onSubmit);
        this.element.removeEventListener('turbo:fetch-request-error', this._onFetchError);
        this.element.removeEventListener('chippie:offline-submit', this._onOfflineSubmit);
    }

    // Runs before Turbo's document-level submit handler, which skips a
    // submission whose default is already prevented. A submit some other
    // script already took over is that script's to announce.
    _submit(event) {
        if (event.defaultPrevented || navigator.onLine || !window.chippiePowerSync) return;
        if (this._onlineOnly(event.target)) return;
        event.preventDefault();
        this._queue(event.target);
    }

    _onlineOnly(form) {
        return 'offlineIntentOnlineOnly' in (form?.dataset || {});
    }

    /** Queue ``form`` as one intent row. Resolves true when it is on the device. */
    async _queue(form) {
        if (!window.chippiePowerSync || this._queueing) return false;
        this._queueing = true;
        const body = document.body.dataset;
        // A form seeded from the device's copy rather than the page names the
        // version it was seeded from (inline_price_controller.js).
        const seeded = 'offlineRecordVersion' in form.dataset ? form.dataset : null;
        try {
            await window.chippiePowerSync.execute(QUEUE_SQL, [
                body.tenantId || '',
                this.operationValue,
                this.targetValue,
                JSON.stringify(this._fields(form)),
                (seeded ? seeded.offlineRecordVersion : body.recordVersion) || '',
                (seeded ? seeded.offlineRecordFields : body.recordFields) || '',
            ]);
            this._closeModal(form);
            document.dispatchEvent(new CustomEvent('powersync:queued'));
            window.showToast?.('Saved on this device. It will sync when you are back online.', 'info');
            return true;
        } catch (error) {
            console.error('[offline-intent] could not queue the form', error);
            window.showToast?.('Could not save this on the device. Try again.', 'error');
            return false;
        } finally {
            this._queueing = false;
        }
    }

    // The form's entries in document order, as the browser would post them,
    // minus the CSRF token (it expires; the upload is authenticated by session),
    // then the URL's own arguments.
    _fields(form) {
        return [...new FormData(form)]
            .filter(([name, value]) => name !== 'csrf_token' && typeof value === 'string')
            .concat(Object.entries(this.paramsValue).map(([name, value]) => [name, String(value)]));
    }

    _closeModal(form) {
        const modal = form.closest('.modal');
        if (!modal || !window.bootstrap) return;
        const instance = window.bootstrap.Modal.getInstance(modal);
        if (instance) instance.hide();
    }
}
